import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  CloudUpload as CloudUploadIcon,
  CloudOff as CloudOffIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { SaveStatusIndicatorProps } from './types';

const formatTimeAgo = (date: Date): string => {
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) {
    return 'just now';
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }

  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

export const SaveStatusIndicator: React.FC<SaveStatusIndicatorProps> = ({
  isSaving,
  lastSaved,
  saveError,
  syncStatus,
  onRetry
}) => {
  const { t } = useTranslation('calibration');

  const getStatusIcon = () => {
    if (isSaving) {
      return <CircularProgress size={16} thickness={4} />;
    }

    if (saveError) {
      return <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />;
    }

    switch (syncStatus) {
      case 'synced':
        return <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />;
      case 'pending':
        return <CloudUploadIcon sx={{ fontSize: 16, color: 'info.main' }} />;
      case 'error':
        return <CloudOffIcon sx={{ fontSize: 16, color: 'error.main' }} />;
      default:
        return null;
    }
  };

  const getStatusText = () => {
    if (isSaving) {
      return t('saving');
    }

    if (saveError) {
      return t('saveFailed');
    }

    if (lastSaved && syncStatus === 'synced') {
      return t('savedAgo', {
        time: formatTimeAgo(lastSaved)
      });
    }

    switch (syncStatus) {
      case 'pending':
        return t('changesPending');
      case 'error':
        return t('syncErrorLabel');
      default:
        return t('autoSaveEnabled');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.5,
        px: 1.5,
        borderRadius: 1,
        backgroundColor: saveError ? 'error.lighter' : 'background.paper',
        border: 1,
        borderColor: saveError ? 'error.light' : 'divider'
      }}
    >
      {getStatusIcon()}
      <Typography
        variant="caption"
        sx={{
          color: saveError ? 'error.main' : 'text.secondary',
          fontWeight: saveError ? 500 : 400
        }}
      >
        {getStatusText()}
      </Typography>
      {saveError && onRetry && (
        <Tooltip title={t('retry')}>
          <IconButton
            size="small"
            onClick={onRetry}
            sx={{ ml: 'auto', p: 0.25 }}
          >
            <RefreshIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export const SaveStatusIndicatorMini: React.FC<
  Pick<SaveStatusIndicatorProps, 'isSaving' | 'syncStatus' | 'saveError'>
> = ({ isSaving, syncStatus, saveError }) => {
  const { t } = useTranslation('calibration');

  if (isSaving) {
    return (
      <Tooltip title={t('saving')}>
        <CircularProgress size={16} thickness={4} />
      </Tooltip>
    );
  }

  if (saveError || syncStatus === 'error') {
    return (
      <Tooltip title={t('syncErrorLabel')}>
        <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
      </Tooltip>
    );
  }

  if (syncStatus === 'synced') {
    return (
      <Tooltip title={t('changesSaved')}>
        <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
      </Tooltip>
    );
  }

  if (syncStatus === 'pending') {
    return (
      <Tooltip title={t('changesPending')}>
        <CloudUploadIcon sx={{ fontSize: 16, color: 'info.main' }} />
      </Tooltip>
    );
  }

  return null;
};
